/**
 * Public Gallery Page - Simplified for quick restoration
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Tag } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import PhotoGallery from '@/components/gallery/PhotoGallery';
import { galleryService } from '@/services/galleryService';
import type { PhotoAlbum, Photo } from '@/types/gallery';

const Gallery: React.FC = () => {
  const { albumId } = useParams<{ albumId?: string }>();
  const [albums, setAlbums] = useState<PhotoAlbum[]>([]);
  const [selectedAlbum, setSelectedAlbum] = useState<PhotoAlbum | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);

  // Load albums and photos
  useEffect(() => {
    if (albumId) {
      loadAlbumWithPhotos(parseInt(albumId));
    } else {
      loadAlbums();
    }
  }, [albumId]);

  const loadAlbums = async (): Promise<void> => {
    try {
      setLoading(true);
      const result = await galleryService.getPhotoAlbums({ isAdmin: false });
      if (result.success) {
        setAlbums(result.data);
      } else {
        console.error('Error loading albums:', result.error);
        setAlbums([]);
      }
    } catch (error) {
      console.error('Error loading albums:', error);
      setAlbums([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAlbumWithPhotos = async (id: number): Promise<void> => {
    try {
      setLoading(true);
      const result = await galleryService.getPhotoAlbum(id, { isAdmin: false });
      if (result.success && result.data) {
        setSelectedAlbum(result.data);
        setPhotos(result.data.photos || []);
      } else {
        console.error('Error loading album:', result.error);
        setSelectedAlbum(null);
        setPhotos([]);
      }
    } catch (error) {
      console.error('Error loading album:', error);
      setSelectedAlbum(null);
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading gallery...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Single album view
  if (selectedAlbum) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => window.history.back()}
              className="mb-4"
            >
              ← Back to Gallery
            </Button>
          </div>

          <PhotoGallery
            photos={photos}
            album={selectedAlbum}
          />
        </div>
      </div>
    );
  }

  // Albums overview
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Photo Gallery</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore our collection of photos from events, programs, and community activities
          </p>
        </div>

        {albums.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Tag className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Gallery Coming Soon</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Our photo gallery is being set up. Check back soon to see photos from our events, 
              programs, and community activities.
            </p>
            <p className="text-sm text-gray-500 mt-4">
              Administrators can add photo albums through the admin dashboard.
            </p>
          </div>
        ) : (
          <div className="flex flex-wrap justify-center gap-8">
            {albums.map((album) => (
              <Card key={album.id} className="overflow-hidden hover:shadow-xl transition-shadow duration-300 w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.34rem)]">
                {album.cover_image ? (
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={album.cover_image}
                      alt={album.title}
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  </div>
                ) : (
                  <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                    <Tag className="h-12 w-12 text-gray-400" />
                  </div>
                )}

                <CardHeader>
                  <CardTitle className="text-xl">{album.title}</CardTitle>
                  {album.description && (
                    <CardDescription className="line-clamp-3">
                      {album.description}
                    </CardDescription>
                  )}
                </CardHeader>

                <CardContent>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{album.photo_count || 0} photos</span>
                    <Button asChild variant="outline" size="sm">
                      <Link to={`/gallery/${album.id}`}>View Album</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Gallery;

